import { useEffect, useState } from 'react';
import type { MailAccountView } from '@epasu/core';
import { crm } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface Props {
  /** null = neues Postfach */
  account: MailAccountView | null;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Postfach einrichten.
 *
 * Beim Bearbeiten bleiben die Passwortfelder leer und das gespeicherte
 * Passwort unangetastet, solange nichts eingetragen wird — die Oberfläche
 * bekommt Passwörter nie zu sehen und könnte sie deshalb auch nicht
 * unverändert zurückschicken.
 */
export function MailAccountDialog({ account, onClose, onSaved }: Props) {
  const [label, setLabel] = useState(account?.label ?? '');
  const [fromName, setFromName] = useState(account?.fromName ?? '');
  const [fromAddress, setFromAddress] = useState(account?.fromAddress ?? '');

  const [imapHost, setImapHost] = useState(account?.imap.host ?? 'imap.strato.de');
  const [imapPort, setImapPort] = useState(String(account?.imap.port ?? 993));
  const [imapUser, setImapUser] = useState(account?.imap.user ?? '');
  const [imapPassword, setImapPassword] = useState('');

  const [smtpHost, setSmtpHost] = useState(account?.smtp.host ?? 'smtp.strato.de');
  const [smtpPort, setSmtpPort] = useState(String(account?.smtp.port ?? 465));
  const [smtpUser, setSmtpUser] = useState(account?.smtp.user ?? '');
  const [smtpPassword, setSmtpPassword] = useState('');

  const [active, setActive] = useState(account?.active ?? true);
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  // Die Anmeldung ist bei Strato und den meisten Anbietern die Adresse selbst.
  // Solange niemand etwas Eigenes einträgt, wird sie mitgeführt.
  const syncUsers = (address: string) => {
    setFromAddress(address);
    if (!account) {
      setImapUser(address);
      setSmtpUser(address);
    }
  };

  const payload = () => ({
    label: label.trim() || fromAddress,
    fromName: fromName.trim() || null,
    fromAddress: fromAddress.trim(),
    active,
    imap: {
      host: imapHost.trim(),
      port: Number(imapPort),
      secure: Number(imapPort) === 993,
      user: imapUser.trim(),
      password: imapPassword,
    },
    smtp: {
      host: smtpHost.trim(),
      port: Number(smtpPort),
      secure: Number(smtpPort) === 465,
      user: smtpUser.trim(),
      password: smtpPassword,
    },
  });

  const test = async () => {
    setBusy(true);
    setTestResult(null);
    setError(null);
    try {
      const p = payload();
      const res = await crm.mailAccounts.test({ imap: p.imap, smtp: p.smtp });
      setTestResult(
        res.smtp && res.imap
          ? 'Beide Verbindungen stehen.'
          : `IMAP ${res.imap ? 'ok' : 'fehlgeschlagen'}, SMTP ${
              res.smtp ? 'ok' : 'fehlgeschlagen'
            }${res.error ? ` — ${res.error}` : ''}`,
      );
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const p = payload();

    if (!p.fromAddress) {
      setError('Bitte eine Absenderadresse angeben.');
      return;
    }
    if (!account && (!p.imap.password || !p.smtp.password)) {
      setError('Für ein neues Postfach werden beide Passwörter gebraucht.');
      return;
    }
    if (!Number.isFinite(p.imap.port) || !Number.isFinite(p.smtp.port)) {
      setError('Die Ports müssen Zahlen sein.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      if (account) await crm.mailAccounts.update(account.id, p);
      else await crm.mailAccounts.create(p);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-center overflow-y-auto bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={account ? 'Postfach bearbeiten' : 'Neues Postfach'}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <form
        onSubmit={(e) => void submit(e)}
        className="my-auto w-full max-w-xl rounded-lg border bg-background p-6 shadow-lg"
      >
        <h2 className="text-lg font-semibold">
          {account ? 'Postfach bearbeiten' : 'Neues Postfach'}
        </h2>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="Absenderadresse" className="sm:col-span-2">
            <Input
              type="email"
              value={fromAddress}
              onChange={(e) => syncUsers(e.target.value)}
              placeholder="info@epasu.de"
            />
          </Field>
          <Field label="Absendername">
            <Input
              value={fromName}
              onChange={(e) => setFromName(e.target.value)}
              placeholder="epasu"
            />
          </Field>
          <Field label="Bezeichnung">
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Info-Postfach"
            />
          </Field>
        </div>

        <fieldset className="mt-5 rounded-md border p-4">
          <legend className="px-1 text-xs font-medium text-muted-foreground">
            IMAP (Abruf)
          </legend>
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Server" className="sm:col-span-2">
              <Input value={imapHost} onChange={(e) => setImapHost(e.target.value)} />
            </Field>
            <Field label="Port">
              <Input value={imapPort} onChange={(e) => setImapPort(e.target.value)} />
            </Field>
            <Field label="Benutzer" className="sm:col-span-2">
              <Input value={imapUser} onChange={(e) => setImapUser(e.target.value)} />
            </Field>
            <Field label={account ? 'Neues Passwort' : 'Passwort'}>
              <Input
                type="password"
                value={imapPassword}
                onChange={(e) => setImapPassword(e.target.value)}
                placeholder={account ? 'unverändert' : ''}
                autoComplete="new-password"
              />
            </Field>
          </div>
        </fieldset>

        <fieldset className="mt-4 rounded-md border p-4">
          <legend className="px-1 text-xs font-medium text-muted-foreground">
            SMTP (Versand)
          </legend>
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Server" className="sm:col-span-2">
              <Input value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} />
            </Field>
            <Field label="Port">
              <Input value={smtpPort} onChange={(e) => setSmtpPort(e.target.value)} />
            </Field>
            <Field label="Benutzer" className="sm:col-span-2">
              <Input value={smtpUser} onChange={(e) => setSmtpUser(e.target.value)} />
            </Field>
            <Field label={account ? 'Neues Passwort' : 'Passwort'}>
              <Input
                type="password"
                value={smtpPassword}
                onChange={(e) => setSmtpPassword(e.target.value)}
                placeholder={account ? 'unverändert' : ''}
                autoComplete="new-password"
              />
            </Field>
          </div>
        </fieldset>

        <label className="mt-4 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
          />
          Aktiv — wird abgerufen
        </label>

        {testResult && (
          <p className="mt-4 rounded-md bg-muted p-3 text-sm">{testResult}</p>
        )}
        {error && (
          <p className="mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            disabled={busy || !imapPassword || !smtpPassword}
            title={
              !imapPassword || !smtpPassword
                ? 'Zum Testen beide Passwörter eintragen'
                : undefined
            }
            onClick={() => void test()}
          >
            Verbindung testen
          </Button>
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? 'Speichere …' : 'Speichern'}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={`block space-y-1.5 ${className ?? ''}`}>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
